// DailyRewardDialog.tsx

import "./DailyRewardDialog.scss";
import DailyRewardVdiv from "../../../assets/icons/DailyRewardDialog/daily-reward-vdiv.svg?react";
import DailyRewardLockIcon from "../../../assets/icons/DailyRewardDialog/daily-reward-lock-icon.svg?react";
import DailyRewardCheckmarkIcon from "../../../assets/icons/DailyRewardDialog/daily-reward-checkmark-icon.svg?react";
import DailyRewardShineRayIcon from "../../../assets/icons/DailyRewardDialog/daily-reward-shine-ray.svg?react";
import DailyRewardFiligreeDecorationIcon from "../../../assets/icons/DailyRewardDialog/daily-reward-filigree-decoration.svg?react";

// TITLES
import DailyRewardTitleEn from "../../../assets/icons/DailyRewardDialog/daily-reward-title-en.svg?react";
import DailyRewardTitleEs from "../../../assets/icons/DailyRewardDialog/daily-reward-title-es.svg?react";
import DailyRewardTitlePtBr from "../../../assets/icons/DailyRewardDialog/daily-reward-title-ptbr.svg?react";
import DailyRewardTitleId from "../../../assets/icons/DailyRewardDialog/daily-reward-title-id.svg?react";
import DailyRewardTitleTh from "../../../assets/icons/DailyRewardDialog/daily-reward-title-th.svg?react";

// COINS
import OrbCoinImg from "../../../assets/images/DailyRewardDialog/orb-coin.webp";
import SushiCoinImg from "../../../assets/images/DailyRewardDialog/sushi-coin.webp";
import OroCoinImg from "../../../assets/images/DailyRewardDialog/oro-coin.webp";
import WldCoinImg from "../../../assets/images/DailyRewardDialog/wld-coin.webp";

// HUMAN ONLY BADGES
import HumanOnlyBadgeEn from "../../../assets/images/human-only-badges/human-only-badge_en.webp";
import HumanOnlyBadgeEs from "../../../assets/images/human-only-badges/human-only-badge_es.webp";
import HumanOnlyBadgePtBr from "../../../assets/images/human-only-badges/human-only-badge_ptbr.webp";
import HumanOnlyBadgeId from "../../../assets/images/human-only-badges/human-only-badge_id.webp";
import HumanOnlyBadgeTh from "../../../assets/images/human-only-badges/human-only-badge_th.webp";

const COIN_IMAGES: Record<string, string> = {
    ORB: OrbCoinImg,
    SUSHI: SushiCoinImg,
    ORO: OroCoinImg,
    WLD: WldCoinImg,
};

import { useTranslation } from "react-i18next";
import { useSlice } from "@bridge-applications/provider-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSpinner,
    faXmark,
} from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import { useEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import EhUniversalVoucher from "../../../abi/EhUniversalVoucher.json";
import { claim } from "../../../claim";
import { NS2 } from "../../../state/config";
import { sendHapticFeedback } from "../../../logic/helpers/world/sendHapticFeedback";

type Lang = "en" | "es" | "pt-br" | "id" | "th";
const titles = {
    en: DailyRewardTitleEn,
    es: DailyRewardTitleEs,
    "pt-br": DailyRewardTitlePtBr,
    id: DailyRewardTitleId,
    th: DailyRewardTitleTh,
} as const;

const humanOnlyBadges = {
    en: HumanOnlyBadgeEn,
    es: HumanOnlyBadgeEs,
    "pt-br": HumanOnlyBadgePtBr,
    id: HumanOnlyBadgeId,
    th: HumanOnlyBadgeTh,
} as const;

type DayStatus = "past" | "today" | "future";
type ClaimStatus = "idle" | "loading" | "success" | "error";

interface DailyRewardDialogProps {
    open: boolean;
    onClose: () => void;
}

export default function DailyRewardDialog({
    open,
    onClose,
}: DailyRewardDialogProps) {
    const { i18n, t } = useTranslation();
    const { state: appState, setSlice } = useSlice("appSlice");
    const Title = titles[i18n.language as Lang] || titles.en;
    const humanOnlyBadge =
        humanOnlyBadges[i18n.language as Lang] || humanOnlyBadges.en;
    const schedule = appState.checkInSchedule;
    const rewards = appState.checkInRewards;
    const [status, setStatus] = useState<ClaimStatus>("idle");
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Lock background scroll while the dialog is open. Without this the page
    // behind the backdrop is still scrollable (and on touch devices the
    // backdrop-blur drifts as the underlying content moves).
    useEffect(() => {
        if (!open) return;
        const prevBody = document.body.style.overflow;
        const prevHtml = document.documentElement.style.overflow;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevBody;
            document.documentElement.style.overflow = prevHtml;
        };
    }, [open]);

    // 500ms after opening, fire a medium haptic so the dialog "lands".
    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => {
            sendHapticFeedback({ type: "medium" });
        }, 500);
        return () => clearTimeout(timer);
    }, [open]);

    if (!open) return null;

    const hasRewards = !!rewards && rewards.length > 0;

    const scheduleResetTo = (next: ClaimStatus, delayMs: number) => {
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        resetTimerRef.current = setTimeout(() => {
            setStatus(next);
            resetTimerRef.current = null;
        }, delayMs);
    };

    const handleClaim = async () => {
        sendHapticFeedback({ type: "light" });

        if (status === "loading" || status === "success") return;
        // No rewards to claim is a no-op; the dialog can only be dismissed
        // via the close button (or auto-closes after a successful claim).
        if (!hasRewards) return;

        const walletAddress = localStorage.getItem(`${NS2}walletAddress`);
        if (!walletAddress) {
            toast.error(t("DailyRewardDialog.signInToClaim"));
            return;
        }

        setStatus("loading");
        try {
            const txs = rewards!
                .map((r) => {
                    const verifyingContract =
                        r.voucher?.domain?.verifyingContract;
                    if (
                        !verifyingContract ||
                        !r.voucher?.struct ||
                        !r.voucher?.signature
                    ) {
                        console.error(
                            "DailyRewardDialog: malformed check-in reward",
                            r,
                        );
                        return null;
                    }
                    return {
                        address: verifyingContract,
                        abi: EhUniversalVoucher.abi,
                        functionName: "claim" as const,
                        args: [r.voucher.struct, r.voucher.signature],
                    };
                })
                .filter(Boolean) as Array<{
                address: string;
                abi: any;
                functionName: "claim";
                args: any[];
            }>;

            if (txs.length === 0) {
                setStatus("error");
                scheduleResetTo("idle", 1800);
                return;
            }

            const res = await claim(txs);

            if ((res as any)?.status === "error") {
                setStatus("error");
                scheduleResetTo("idle", 1800);
                return;
            }

            setStatus("success");
            sendHapticFeedback({ type: "heavy" });
            // NOTE: don't clear the check-in slice yet. Layout.tsx drives
            // this dialog's `open` prop from `checkInVoucherAvailable` /
            // `checkInRewards`, so nulling them immediately would flip
            // `open` → false, hit the early-return and unmount us right
            // away — the user would never see the success state or the
            // current day flipping to "past" / checked. We defer the
            // slice clear into the close timeout below.
            setTimeout(() => {
                setSlice({
                    checkInVoucherAvailable: null,
                    checkInRewards: null,
                });
                onClose();
                setStatus("idle");
            }, 2600);
        } catch (err) {
            console.error("DailyRewardDialog claim error:", err);
            setStatus("error");
            scheduleResetTo("idle", 1800);
        }
    };

    const tapLabel =
        status === "loading"
            ? t("DailyRewardDialog.claiming")
            : status === "success"
              ? t("DailyRewardDialog.claimed")
              : status === "error"
                ? t("DailyRewardDialog.claimFailed")
                : t("DailyRewardDialog.tapToClaim");

    return (
        <div className="daily-reward-dialog">
            <svg
                width="0"
                height="0"
                aria-hidden="true"
                focusable="false"
                style={{
                    position: "absolute",
                    width: 0,
                    height: 0,
                    pointerEvents: "none",
                }}
            >
                <defs>
                    <filter id="drd-shine-ray-blur">
                        <feGaussianBlur stdDeviation="0.4" />
                    </filter>
                </defs>
            </svg>
            <div className="drd__backdrop">
                <button
                    type="button"
                    className="drd__close"
                    aria-label={t("DailyRewardDialog.close")}
                    onClick={() => onClose()}
                >
                    <FontAwesomeIcon
                        className="drd__close__icon"
                        icon={faXmark}
                    />
                </button>
                <div
                    className="drd__content"
                    data-claim-status={status}
                    onClick={() => handleClaim()}
                >
                    <DailyRewardFiligreeDecorationIcon
                        className="drd__content__filigree drd__content__filigree--tl"
                        aria-hidden="true"
                    />
                    <DailyRewardFiligreeDecorationIcon
                        className="drd__content__filigree drd__content__filigree--tr"
                        aria-hidden="true"
                    />
                    <DailyRewardFiligreeDecorationIcon
                        className="drd__content__filigree drd__content__filigree--bl"
                        aria-hidden="true"
                    />
                    <DailyRewardFiligreeDecorationIcon
                        className="drd__content__filigree drd__content__filigree--br"
                        aria-hidden="true"
                    />

                    <div className="drd__content__title">
                        <Title />
                    </div>

                    <div className="drd__content__human-badge">
                        <img
                            className="drd__content__human-badge__img"
                            src={humanOnlyBadge}
                            alt={t("DailyRewardDialog.humanOnlyBadge")}
                        />
                    </div>

                    <div className="drd__content__vdiv">
                        <DailyRewardVdiv />
                    </div>

                    {schedule && (
                        <div className="drd__content__schedule">
                            {schedule.days.map((day, idx) => {
                                const isCurrentDay =
                                    idx === schedule.currentDayIndex;
                                // Once the claim succeeds, treat today as
                                // "past" too so the card immediately shows
                                // the dimmed/checkmark state before the
                                // dialog auto-closes.
                                const dayStatus: DayStatus = isCurrentDay
                                    ? status === "success"
                                        ? "past"
                                        : "today"
                                    : day.claimed
                                      ? "past"
                                      : "future";

                                const span = Math.min(
                                    Math.max(day.rewards.length, 1),
                                    3,
                                );

                                return (
                                    <div
                                        key={idx}
                                        className="drd__day-cell"
                                        data-span={span}
                                        data-status={dayStatus}
                                    >
                                        <div
                                            className="drd__day"
                                            data-status={dayStatus}
                                        >
                                            <div className="drd__day__inner">
                                                <div className="drd__day__title">
                                                    <p>
                                                        {dayStatus === "today"
                                                            ? t(
                                                                  "DailyRewardDialog.today",
                                                              )
                                                            : t(
                                                                  "DailyRewardDialog.dayN",
                                                                  {
                                                                      num:
                                                                          idx +
                                                                          1,
                                                                  },
                                                              )}
                                                    </p>
                                                </div>

                                                <div className="drd__day__rewards">
                                                    {day.rewards.map(
                                                        (reward, rIdx) => {
                                                            const coinImg =
                                                                reward.label
                                                                    ? COIN_IMAGES[
                                                                          reward
                                                                              .label
                                                                      ]
                                                                    : undefined;
                                                            const leadingZero =
                                                                /^0([.,])(.*)$/.exec(
                                                                    reward.amount,
                                                                );
                                                            return (
                                                                <div
                                                                    key={rIdx}
                                                                    className="drd__day__reward"
                                                                >
                                                                    {coinImg && (
                                                                        <div className="drd__day__reward__coin-wrap">
                                                                            {dayStatus ===
                                                                                "today" && (
                                                                                <div
                                                                                    className="drd__day__reward__shine"
                                                                                    aria-hidden="true"
                                                                                >
                                                                                    {Array.from(
                                                                                        {
                                                                                            length: 10,
                                                                                        },
                                                                                    ).map(
                                                                                        (
                                                                                            _,
                                                                                            i,
                                                                                        ) => (
                                                                                            <DailyRewardShineRayIcon
                                                                                                key={
                                                                                                    i
                                                                                                }
                                                                                                className="drd__day__reward__shine__ray"
                                                                                                style={{
                                                                                                    transform: `translate(-50%, 0) rotate(${i * 36}deg)`,
                                                                                                }}
                                                                                            />
                                                                                        ),
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                            <img
                                                                                className="drd__day__reward__coin"
                                                                                src={
                                                                                    coinImg
                                                                                }
                                                                                alt={
                                                                                    reward.label ??
                                                                                    ""
                                                                                }
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    <span className="drd__day__reward__amount">
                                                                        {leadingZero ? (
                                                                            <>
                                                                                <span className="drd__day__reward__amount__lead">
                                                                                    {`0${leadingZero[1]}`}
                                                                                </span>
                                                                                {
                                                                                    leadingZero[2]
                                                                                }
                                                                            </>
                                                                        ) : (
                                                                            reward.amount
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            );
                                                        },
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {dayStatus === "future" && (
                                            <DailyRewardLockIcon className="drd__day-cell__lock-icon" />
                                        )}
                                        {dayStatus === "past" && (
                                            <DailyRewardCheckmarkIcon className="drd__day-cell__check-icon" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div
                    className="drd__tap-to-claim"
                    data-claim-status={status}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClaim();
                    }}
                >
                    {status === "loading" && (
                        <FontAwesomeIcon
                            className="drd__tap-to-claim__icon drd__tap-to-claim__icon--spin"
                            icon={faSpinner}
                        />
                    )}
                    {status === "error" && (
                        <FontAwesomeIcon
                            className="drd__tap-to-claim__icon"
                            icon={faXmark}
                        />
                    )}
                    <span>{tapLabel}</span>
                </div>
            </div>
            <Toaster />
        </div>
    );
}
